import { useMutation } from "@tanstack/react-query";
import { pollStatus } from "@/features/identity-hub/api/identity-hub.api";
import {
  matchScriptForSet,
  type ScriptMatchResult,
} from "@/features/script-review/api/script-review.api";

/**
 * Transcription can take ~30s, and the job shares the worker's slow lane, so
 * give it ~90s before giving up. Timing out isn't a failure state worth
 * shouting about — it just means the admin picks the script by hand, which is
 * what they did before this feature existed.
 */
const MATCH_POLL_ATTEMPTS = 25;
const MATCH_POLL_INTERVAL_MS = 500;

/**
 * Fires the auto-match and polls for the verdict.
 *
 * Deliberately NOT useAmberMutation: that toasts on success and error, which is
 * wrong for a background suggestion — the answer belongs inline next to the
 * script dropdown, and a failure should whisper, not pop.
 */
export function useScriptMatch(
  onResult: (result: ScriptMatchResult | null) => void,
) {
  return useMutation({
    mutationFn: async (input: {
      tenantId: string;
      uploadId: string;
      setId: string;
    }): Promise<ScriptMatchResult> => {
      const { correlationId } = await matchScriptForSet(input);
      const status = await pollStatus(
        "/script-reviews",
        correlationId,
        MATCH_POLL_ATTEMPTS,
        MATCH_POLL_INTERVAL_MS,
      );
      if (status.status === "FAILED") {
        throw new Error("The match job failed");
      }
      return status as unknown as ScriptMatchResult;
    },
    onSuccess: (result) => onResult(result),
    // A dead match must never block the form — fall back to manual selection.
    onError: () => onResult(null),
  });
}
